import styles from './WeChatInformer.module.scss';
import ChainIcon from '@assets/icons/chain_62.svg';
import cn from 'classnames';
import CompletionModalWindow from '@components/modals/CompletionModalWindow';
import FormErrorMessage from '@components/FormErrorMessage';
import QrIcon from '@assets/icons/qr_62.svg';
import Title from '@components/Title';
import { checkEntry, checkEntryResponse, getWeChatQrCode } from '@api/apiClient/weChatQrCode';
import { IWeChatInformerProps } from './types';
import { Loader } from '@components/Loader';
import { Modal } from '@components/Modal';
import { Text, TextColor, TextSize, TextWeight } from '@components/Text';
import { useAuth } from '@contexts/AuthenticationContext';
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';

const WeChatInformer: React.FC<IWeChatInformerProps> = (props: IWeChatInformerProps): React.ReactElement => {
  const {
    action,
    calledFrom,
    informerDescription,
    informerTitle,
    promoId,
    refetchWallets,
  } = props;

  const [isOpenNotLinkedWindow, setIsOpenNotLinkedWindow] = useState<boolean>(false);
  const [isOpenRefreshQrCodeWindow, setIsOpenRefreshQrCodeWindow] = useState<boolean>(false);

  const auth = useAuth();
  const { t } = useTranslation();

  const timeoutCheckQrCode = useRef<number | null>(null);
  const timeoutCheckEntry = useRef<number | null>(null);

  const {
    data: dataGetWeChatQrCode,
    error: errorGetWeChatQrCode,
    isError: isErrorGetWeChatQrCode,
    isFetching: isFetchingGetWeChatQrCode,
    refetch: refetchGetWeChatQrCode
  } = useQuery({
    queryKey: ['getWeChatQrCode', calledFrom, promoId],
    queryFn: () => getWeChatQrCode(calledFrom, promoId),
    retry: 1
  });

  const { mutate: mutateCheckEntry } = useMutation({
    mutationFn: checkEntry
  });

  const waitingCheckEntry = (authTicket: string): void => {
    clearTimeout(timeoutCheckEntry.current as number);
    timeoutCheckEntry.current = window.setTimeout(() => {
      mutateCheckEntry(authTicket, {
        onSuccess: (data: checkEntryResponse) => {
          if (data) {
            const { accessToken, authTicket, phone, status } = data;
            if (status === 'Confirmed') {
              if (calledFrom === 'linktologin') {
                action!({
                  authTicket,
                  phone: phone!
                });
                if (refetchWallets) refetchWallets();
              }
              if (calledFrom === 'login' || calledFrom === 'signup') auth.entry(accessToken!);
            }
            if (status === 'Expired') setIsOpenRefreshQrCodeWindow(true);
            if (status === 'FailAccountNotLinked') setIsOpenNotLinkedWindow(true);
            if (status === 'Waiting') waitingCheckEntry(authTicket);
          } else {
            waitingCheckEntry(authTicket);
          }
        }
      });
    }, 3000);
  };

  useEffect(() => {
    if (timeoutCheckEntry.current) clearTimeout(timeoutCheckEntry.current as number);
    if (timeoutCheckQrCode.current) clearTimeout(timeoutCheckQrCode.current as number);
    if (dataGetWeChatQrCode) {
      timeoutCheckQrCode.current = window.setTimeout(() => {
        setIsOpenRefreshQrCodeWindow(true);
      }, 85800000);
      waitingCheckEntry(dataGetWeChatQrCode.authTicket);
    }
    return () => {
      clearTimeout(timeoutCheckEntry.current as number);
      clearTimeout(timeoutCheckQrCode.current as number);
    };
  }, [dataGetWeChatQrCode]);

  const condition: boolean = calledFrom === 'login' || calledFrom === 'signup';

  return (
      <div
          className={cn(styles.root, {
            [styles.rootAbsolute]: condition,
            [styles.rootColumn]: condition,
            [styles.rootCard]: !condition
          })}
      >
        {condition && <Title title={informerTitle}/>}
        <div className={cn(styles.qrCode, condition ? styles.qrCodeColumn : styles.qrCodeCard)}>
          {isFetchingGetWeChatQrCode ? (
              <Loader
                  dark
                  size="xs"
              />
          ) : (
              <>
                {dataGetWeChatQrCode && (
                    <img
                        className={styles.qrCodeSelf}
                        alt="qrCode"
                        src={`data:image/jpeg;base64,${dataGetWeChatQrCode.qrCode}`}
                    />
                )}
                {isErrorGetWeChatQrCode && <FormErrorMessage error={errorGetWeChatQrCode}/>}
              </>
          )}
        </div>
        {condition ? (
            <Text
                color={TextColor.PRIMARY}
                size={TextSize.S}
            >
              {informerDescription}
            </Text>
        ) : (
            <div className={styles.info}>
              <Text
                  color={TextColor.PRIMARY}
                  size={TextSize.L}
                  weight={TextWeight.BOLD}
              >
                {informerTitle}
              </Text>
              <Text
                  color={TextColor.PRIMARY}
                  size={TextSize.S}
              >
                {informerDescription}
              </Text>
            </div>
        )}
        <Modal
            isOpen={isOpenRefreshQrCodeWindow}
            onClickOverlay={() => setIsOpenRefreshQrCodeWindow(false)}
            onClose={() => setIsOpenRefreshQrCodeWindow(false)}
        >
          <CompletionModalWindow
              btnText={t('weChatInformer.refreshModal.btnText')}
              description={t('weChatInformer.refreshModal.description')}
              handlerSetIsOpenModalWindow={() => {
                refetchGetWeChatQrCode();
                setIsOpenRefreshQrCodeWindow(false);
              }}
              icon={QrIcon}
              title={t('weChatInformer.refreshModal.title')}
          />
        </Modal>
        <Modal
            isOpen={isOpenNotLinkedWindow}
            onClickOverlay={() => setIsOpenNotLinkedWindow(false)}
            onClose={() => setIsOpenNotLinkedWindow(false)}
        >
          <CompletionModalWindow
              btnText={t('weChatInformer.notLinkedModal.btnText')}
              description={t('weChatInformer.notLinkedModal.description')}
              handlerSetIsOpenModalWindow={() => setIsOpenNotLinkedWindow(false)}
              icon={ChainIcon}
              title={t('weChatInformer.notLinkedModal.title')}
          />
        </Modal>
      </div>
  );
};

export default WeChatInformer;
